// src/pricing/rates.ts
// Central config: zones + LONG/SHORT (OUT/IN) + DOMESTIC + overrides

export type RateRow = {
  perKm: number;
  min?: number;
  fixed?: number;
  note?: string;
};

// =====================
//  PODZIAŁ NA STREFY
// =====================

// 4-way (N/E/S/W)
export const Z4 = {
  PL: ["PL-N", "PL-E", "PL-S", "PL-W"] as const,
  DE: ["DE-N", "DE-E", "DE-S", "DE-W"] as const,
  FR: ["FR-N", "FR-E", "FR-S", "FR-W"] as const,
  ES: ["ES-N", "ES-E", "ES-S", "ES-W"] as const,
} as const;

// 2-way (N/S) — UK trzymamy jako GB
export const Z2 = {
  IT: ["IT-N", "IT-S"] as const,
  GB: ["GB-N", "GB-S"] as const,
  SE: ["SE-N", "SE-S"] as const,
  NO: ["NO-N", "NO-S"] as const,
  HU: ["HU-N", "HU-S"] as const,
} as const;

// Bez podziału (1 strefa = kod kraju)
// Uwaga: NIE dodawaj tu krajów, które masz w Z4/Z2 (PL, DE, FR, ES, IT, GB, SE, NO, HU)
export const Z1 = [
  "AL","AT","BA","BE","BG","BY","CH","CZ","DK","EE","FI","GR","HR","IE",
  "LT","LU","LV","MC","MD","ME","MK","MT","NL","PT","RO","RS","SI",
  "SK","SM","TR","UA","XK","LI","IS","CY","AD","VA"
] as const;

// Wszystkie strefy w systemie
export const ALL_ZONES = [
  ...Z4.PL, ...Z4.DE, ...Z4.FR, ...Z4.ES,
  ...Z2.IT, ...Z2.GB, ...Z2.SE, ...Z2.NO, ...Z2.HU,
  ...Z1,
] as const;

export type Zone = typeof ALL_ZONES[number];
export type DirectionKey = `${Zone}|${Zone}`;

// =====================
// LONG (>= 300 km) OUT/IN
// =====================
export const OUT_PER_KM: Record<Zone, number> = Object.fromEntries(
  ALL_ZONES.map(z => [z, 0])
) as Record<Zone, number>;

export const IN_PER_KM: Record<Zone, number> = Object.fromEntries(
  ALL_ZONES.map(z => [z, 0])
) as Record<Zone, number>;

// =====================
// SHORT (< 300 km) OUT/IN
// (auto-fill z LONG na końcu pliku)
// =====================
export const OUT_PER_KM_SHORT: Record<Zone, number> = Object.fromEntries(
  ALL_ZONES.map(z => [z, 0])
) as Record<Zone, number>;

export const IN_PER_KM_SHORT: Record<Zone, number> = Object.fromEntries(
  ALL_ZONES.map(z => [z, 0])
) as Record<Zone, number>;

// =====================
// DOMESTIC (ten sam kraj)
// klucz = kod kraju: PL, DE, ES, FR, IT, GB, itd.
// =====================
export const DOM_PER_KM: Record<string, number> = {
  PL: 0.40,
  DE: 0.40,
  FR: 0.40,
  ES: 0.40,
  IT: 0.40,
  GB: 0.40,
  SE: 0.40,
  NO: 0.40,
  HU: 0.40,

  NL: 0.40,
  BE: 0.40,
  AT: 0.40,
  CZ: 0.40,
  SK: 0.40,
  RO: 0.40,
  BG: 0.40,
  DK: 0.40,
  FI: 0.40,
  IE: 0.40,
  PT: 0.40,
  GR: 0.40,
  HR: 0.40,
  SI: 0.40,
  LT: 0.40,
  LV: 0.40,
  EE: 0.40,
  CH: 0.40,
};

// =====================
//  OUT / IN (LONG) – wpisy
// =====================

// PL
OUT_PER_KM["PL-N"] = 0.60;
OUT_PER_KM["PL-E"] = 0.60;
OUT_PER_KM["PL-S"] = 0.60;
OUT_PER_KM["PL-W"] = 0.60;

IN_PER_KM["PL-N"]  = 0.50;
IN_PER_KM["PL-E"]  = 0.50;
IN_PER_KM["PL-S"]  = 0.50;
IN_PER_KM["PL-W"]  = 0.50;

// DE
OUT_PER_KM["DE-N"] = 0.70;
OUT_PER_KM["DE-E"] = 0.70;
OUT_PER_KM["DE-S"] = 0.70;
OUT_PER_KM["DE-W"] = 0.70;

IN_PER_KM["DE-N"]  = 0.90;
IN_PER_KM["DE-E"]  = 0.90;
IN_PER_KM["DE-S"]  = 0.90;
IN_PER_KM["DE-W"]  = 0.90;

// FR
OUT_PER_KM["FR-N"] = 0.70;
OUT_PER_KM["FR-E"] = 0.70;
OUT_PER_KM["FR-S"] = 0.70;
OUT_PER_KM["FR-W"] = 0.90;

IN_PER_KM["FR-N"]  = 0.85;
IN_PER_KM["FR-E"]  = 0.85;
IN_PER_KM["FR-S"]  = 0.85;
IN_PER_KM["FR-W"]  = 0.95;

// ES
OUT_PER_KM["ES-N"] = 0.65;
OUT_PER_KM["ES-E"] = 0.65;
OUT_PER_KM["ES-S"] = 0.65;
OUT_PER_KM["ES-W"] = 0.65;

IN_PER_KM["ES-N"]  = 0.85;
IN_PER_KM["ES-E"]  = 0.85;
IN_PER_KM["ES-S"]  = 0.95;
IN_PER_KM["ES-W"]  = 0.85;

// IT
OUT_PER_KM["IT-N"] = 0.70;
OUT_PER_KM["IT-S"] = 0.70;

IN_PER_KM["IT-N"]  = 0.85;
IN_PER_KM["IT-S"]  = 0.95;

// GB (dopłaty prom/cło masz osobno)
OUT_PER_KM["GB-N"] = 0.50;
OUT_PER_KM["GB-S"] = 0.50;

IN_PER_KM["GB-N"]  = 0.95;
IN_PER_KM["GB-S"]  = 0.85;

// SE
OUT_PER_KM["SE-N"] = 0.60;
OUT_PER_KM["SE-S"] = 0.70;

IN_PER_KM["SE-N"]  = 1.10;
IN_PER_KM["SE-S"]  = 0.95;

// NO
OUT_PER_KM["NO-N"] = 0.50;
OUT_PER_KM["NO-S"] = 0.60;

IN_PER_KM["NO-N"]  = 1.10;
IN_PER_KM["NO-S"]  = 0.95;

// HU
OUT_PER_KM["HU-N"] = 0.70;
OUT_PER_KM["HU-S"] = 0.70;

IN_PER_KM["HU-N"]  = 0.95;
IN_PER_KM["HU-S"]  = 0.95;

// =====================
// Z1 – single-zone countries (LONG) – OUT / IN per country
// =====================

// BENELUX
OUT_PER_KM["BE"] = 0.70; IN_PER_KM["BE"] = 1.15;
OUT_PER_KM["NL"] = 0.70; IN_PER_KM["NL"] = 1.15;
OUT_PER_KM["LU"] = 0.70; IN_PER_KM["LU"] = 1.15;

// DACH / ALPS (single-zone in your model)
OUT_PER_KM["AT"] = 0.70; IN_PER_KM["AT"] = 1.12;
OUT_PER_KM["CH"] = 0.70; IN_PER_KM["CH"] = 1.20;
OUT_PER_KM["LI"] = 0.70; IN_PER_KM["LI"] = 1.15;

// CEE (single-zone)
OUT_PER_KM["CZ"] = 0.50; IN_PER_KM["CZ"] = 0.95;
OUT_PER_KM["SK"] = 0.50; IN_PER_KM["SK"] = 0.95;
OUT_PER_KM["RO"] = 0.50; IN_PER_KM["RO"] = 0.95;
OUT_PER_KM["BG"] = 0.50; IN_PER_KM["BG"] = 0.95;

// BALTICS
OUT_PER_KM["LT"] = 0.40; IN_PER_KM["LT"] = 0.95;
OUT_PER_KM["LV"] = 0.40; IN_PER_KM["LV"] = 0.95;
OUT_PER_KM["EE"] = 0.40; IN_PER_KM["EE"] = 0.98;

// NORDICS / ISLANDS (single-zone in your Z1)
OUT_PER_KM["DK"] = 0.80; IN_PER_KM["DK"] = 0.90;
OUT_PER_KM["FI"] = 0.80; IN_PER_KM["FI"] = 1.05;
OUT_PER_KM["IS"] = 0.80; IN_PER_KM["IS"] = 1.45;

// IBERIA single-zone
OUT_PER_KM["PT"] = 0.60; IN_PER_KM["PT"] = 0.95;

// GREECE / ADRIATIC
OUT_PER_KM["GR"] = 1.05; IN_PER_KM["GR"] = 1.05;
OUT_PER_KM["SI"] = 1.12; IN_PER_KM["SI"] = 1.00;
OUT_PER_KM["HR"] = 1.08; IN_PER_KM["HR"] = 0.85;

// IRELAND
OUT_PER_KM["IE"] = 1.25; IN_PER_KM["IE"] = 1.25;

// MICROSTATES / ISLANDS
OUT_PER_KM["MC"] = 1.25; IN_PER_KM["MC"] = 1.20;
OUT_PER_KM["SM"] = 1.20; IN_PER_KM["SM"] = 1.15;
OUT_PER_KM["MT"] = 1.30; IN_PER_KM["MT"] = 1.30;
OUT_PER_KM["CY"] = 1.30; IN_PER_KM["CY"] = 1.30;
OUT_PER_KM["VA"] = 1.30; IN_PER_KM["VA"] = 1.30;

// BALKANS (non-EU / mixed)
OUT_PER_KM["AL"] = 1.00; IN_PER_KM["AL"] = 0.95;
OUT_PER_KM["BA"] = 1.02; IN_PER_KM["BA"] = 0.95;
OUT_PER_KM["ME"] = 1.02; IN_PER_KM["ME"] = 0.95;
OUT_PER_KM["MK"] = 1.02; IN_PER_KM["MK"] = 0.95;
OUT_PER_KM["RS"] = 1.05; IN_PER_KM["RS"] = 0.95;
OUT_PER_KM["XK"] = 1.02; IN_PER_KM["XK"] = 0.95;
OUT_PER_KM["MD"] = 1.08; IN_PER_KM["MD"] = 0.95;

// ANDORRA (góry)
OUT_PER_KM["AD"] = 1.05; IN_PER_KM["AD"] = 1.05;

// BELARUS / TURKEY / UKRAINE (startowe)
OUT_PER_KM["BY"] = 1.25; IN_PER_KM["BY"] = 1.05;
OUT_PER_KM["TR"] = 1.20; IN_PER_KM["TR"] = 1.10;
OUT_PER_KM["UA"] = 1.25; IN_PER_KM["UA"] = 1.05;

// =====================
// SHORT modifiers + AUTO-FILL SHORT from LONG
// (<300 km staje się droższe o stałą dopłatę)
// =====================
const SHORT_ADD_OUT = 0.15;
const SHORT_ADD_IN  = 0.15;

ALL_ZONES.forEach(z => {
  OUT_PER_KM_SHORT[z] = OUT_PER_KM[z] ? OUT_PER_KM[z] + SHORT_ADD_OUT : 0;
  IN_PER_KM_SHORT[z]  = IN_PER_KM[z]  ? IN_PER_KM[z]  + SHORT_ADD_IN  : 0;
});

// Jeśli chcesz wyjątki dla short, dopisz je PONIŻEJ tej pętli, np.:
// OUT_PER_KM_SHORT["ES-N"] = 0.95;
// IN_PER_KM_SHORT["ES-N"]  = 1.35;

// =====================
// OVERRIDES (rzadkie wyjątki kierunkowe)
// =====================
export const OVERRIDES: Partial<Record<DirectionKey, RateRow>> = {
  // "ES-N|PL-E": { perKm: 0.95, note: "korytarz specjalny" },
  // "PL-E|ES-N": { perKm: 1.25, note: "korytarz specjalny" },
};

// =====================
// Fallbacki (gdy OUT/IN zostawione na 0)
// =====================
export const DEFAULT_DOM: RateRow = { perKm: 0 };
export const DEFAULT_INTL: RateRow = { perKm: 0 };
